import React, { useState, useRef, useEffect } from 'react';

interface Props {
  from: string;
  to: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  labelFrom: string;
  labelTo: string;
}

const MONTHS = ['January','February','March','April','May','June','July',
  'August','September','October','November','December'];
const WDAYS = ['Mo','Tu','We','Th','Fr','Sa','Su'];

function parseYMD(s: string): Date | null {
  if (!s) return null;
  const d = new Date(s + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
}

function toYMD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function fmtDisplay(d: Date): string {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function sameDay(a: Date | null, b: Date | null) {
  return !!a && !!b
    && a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

const navBtn: React.CSSProperties = {
  width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border-soft)',
  background: 'transparent', cursor: 'pointer', fontSize: 16, lineHeight: 1,
  color: 'var(--text-muted)', fontFamily: 'inherit', display: 'flex',
  alignItems: 'center', justifyContent: 'center',
};

export const DateRangePicker: React.FC<Props> = ({
  from, to, onFromChange, onToChange, labelFrom, labelTo,
}) => {
  const fromDate = parseYMD(from);
  const toDate   = parseYMD(to);
  const today    = new Date(); today.setHours(0, 0, 0, 0);

  const [open, setOpen]     = useState<'from' | 'to' | null>(null);
  const [view, setView]     = useState<Date>(() => fromDate ?? new Date());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openCal = (field: 'from' | 'to') => {
    if (open === field) { setOpen(null); return; }
    const anchor = field === 'from' ? fromDate : toDate;
    setView(anchor ? new Date(anchor.getFullYear(), anchor.getMonth(), 1) : new Date());
    setOpen(field);
  };

  const pick = (d: Date) => {
    if (open === 'from') {
      onFromChange(toYMD(d));
      if (toDate && d > toDate) onToChange('');
      setOpen(null);
      setTimeout(() => {
        setView(new Date(d.getFullYear(), d.getMonth(), 1));
        setOpen('to');
      }, 80);
    } else {
      if (fromDate && d < fromDate) onFromChange(toYMD(d));
      else onToChange(toYMD(d));
      setOpen(null);
    }
  };

  const buildGrid = () => {
    const y = view.getFullYear(), m = view.getMonth();
    const first = new Date(y, m, 1);
    let dow = first.getDay(); dow = dow === 0 ? 6 : dow - 1;
    const dim  = new Date(y, m + 1, 0).getDate();
    const dprev = new Date(y, m, 0).getDate();
    const total = Math.ceil((dow + dim) / 7) * 7;
    const cells: React.ReactNode[] = [];

    for (let i = 0; i < total; i++) {
      let day: number, cd: Date, other = false;
      if (i < dow)           { day = dprev - dow + 1 + i; cd = new Date(y, m-1, day); other = true; }
      else if (i >= dow+dim) { day = i - dow - dim + 1;   cd = new Date(y, m+1, day); other = true; }
      else                   { day = i - dow + 1;          cd = new Date(y, m,   day); }

      const isSel   = sameDay(cd, fromDate) || sameDay(cd, toDate);
      const inRange = !isSel && !!fromDate && !!toDate && cd > fromDate && cd < toDate;
      const rStart  = inRange && sameDay(new Date(cd.getTime() - 86400000), fromDate);
      const rEnd    = inRange && sameDay(new Date(cd.getTime() + 86400000), toDate);
      const isToday = sameDay(cd, today);

      let bg = 'transparent';
      let color = 'var(--text-primary)';
      let radius: string = '6px';
      let fw: number = isToday ? 800 : 500;

      if (isSel)   { bg = 'var(--brand)'; color = '#fff'; fw = 700; }
      else if (inRange) {
        bg = '#dbeafe'; color = '#1e40af';
        radius = rStart ? '6px 0 0 6px' : rEnd ? '0 6px 6px 0' : '0';
      }
      if (other)   { color = 'var(--text-disabled)'; }
      if (isToday && !isSel) { color = 'var(--brand)'; }

      cells.push(
        <button
          key={i}
          onClick={() => !other && pick(cd)}
          style={{
            aspectRatio: '1', border: 'none', background: bg, color, fontWeight: fw,
            borderRadius: radius, cursor: other ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontFamily: 'inherit', opacity: other ? 0.4 : 1,
            pointerEvents: other ? 'none' : 'auto', transition: 'background 0.1s',
          }}
        >
          {day}
        </button>
      );
    }
    return cells;
  };

  const popup = (alignRight: boolean) => (
    <div
      onClick={e => e.stopPropagation()}
      style={{
        position: 'absolute', top: 'calc(100% + 8px)',
        left: alignRight ? 'auto' : 0, right: alignRight ? 0 : 'auto',
        background: 'var(--bg-surface)', border: '1px solid var(--border-soft)',
        borderRadius: 14, boxShadow: '0 8px 30px rgba(0,0,0,0.13)',
        padding: 16, width: 280, zIndex: 300,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button style={navBtn} onClick={() => setView(v => new Date(v.getFullYear(), v.getMonth()-1, 1))}>‹</button>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
          {MONTHS[view.getMonth()]} {view.getFullYear()}
        </span>
        <button style={navBtn} onClick={() => setView(v => new Date(v.getFullYear(), v.getMonth()+1, 1))}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: 4 }}>
        {WDAYS.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-disabled)', padding: '4px 0' }}>
            {d}
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
        {buildGrid()}
      </div>
    </div>
  );

  const focused = open !== null;
  const fieldStyle = (active: boolean): React.CSSProperties => ({
    flex: 1, display: 'flex', flexDirection: 'column', padding: '10px 14px',
    cursor: 'pointer', borderRadius: 9, userSelect: 'none', minWidth: 0,
    background: active ? 'var(--brand-faint)' : 'transparent',
    transition: 'background 0.12s', position: 'relative',
  });

  return (
    <div ref={ref} style={{
      display: 'flex', alignItems: 'stretch', position: 'relative',
      border: `1.5px solid ${focused ? 'var(--brand)' : 'var(--border-input)'}`,
      borderRadius: 10, background: 'var(--bg-surface)',
      boxShadow: focused ? '0 0 0 3px var(--brand-light)' : 'none',
      transition: 'border-color 0.15s, box-shadow 0.15s',
    }}>
      <div style={fieldStyle(open === 'from')} onClick={() => openCal('from')}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.7px',
          textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 3 }}>
          {labelFrom}
        </div>
        <div style={{ fontSize: 14, fontWeight: 500, color: from ? 'var(--text-primary)' : 'var(--text-disabled)', whiteSpace: 'nowrap' }}>
          {fromDate ? fmtDisplay(fromDate) : '—'}
        </div>
        {open === 'from' && popup(false)}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 36, flexShrink: 0, borderLeft: '1px solid var(--border-soft)',
        borderRight: '1px solid var(--border-soft)', background: 'var(--bg-page)',
        color: 'var(--text-disabled)', fontSize: 16, pointerEvents: 'none' }}>
        →
      </div>

      <div style={fieldStyle(open === 'to')} onClick={() => openCal('to')}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.7px',
          textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 3 }}>
          {labelTo}
        </div>
        <div style={{ fontSize: 14, fontWeight: 500, color: to ? 'var(--text-primary)' : 'var(--text-disabled)', whiteSpace: 'nowrap' }}>
          {toDate ? fmtDisplay(toDate) : '—'}
        </div>
        {open === 'to' && popup(true)}
      </div>
    </div>
  );
};
